<template>
    <div class="chat-function-interaction-item">
        <svg-icon
            size="xs"
            :icon="iconForFunction"
            :color="colorForFunction"
            is-custom-icon
        />
        <div class="chat-function-interaction-item__title">
            {{ titleForFunction }}
        </div>
        <div class="chat-function-interaction-item__link">
            <button
                type="button"
                @click="showFunctionInformation"
                @keydown.enter="showFunctionInformation"
            >
                <text-decorator bold :size="FontSize.xs">
                    Bekijken
                </text-decorator>
            </button>
        </div>
    </div>

    <modal-dialog
        v-if="localInteraction"
        ref="modal"
        :label="titleForFunction"
        :description="titleForFunction"
        :size="5"
    >
        <flex-container direction="column" align-content="stretch">
            <component
                :is="contextComponentForFunction"
                :title="titleForFunction"
                :interaction="localInteraction"
                :arguments="argumentsForFunction"
                :result="resultForFunction"
            />
        </flex-container>
    </modal-dialog>
</template>

<script setup lang="ts">
    import EyeIcon from '@keyvue/one-awesome-icons/icons/regular/eye.svg?component';
    import MapLocationDotIcon from '@keyvue/one-awesome-icons/icons/regular/map-location-dot.svg?component';
    import EnvelopeIcon from '@keyvue/one-awesome-icons/icons/regular/envelope.svg?component';
    import CommentDotsIcon from '@keyvue/one-awesome-icons/icons/regular/comment-dots.svg?component';
    import MapIcon from '@keyvue/one-awesome-icons/icons/regular/map.svg?component';
    import BoltIcon from '@keyvue/one-awesome-icons/icons/regular/bolt.svg?component';
    import HouseIcon from '@keyvue/one-awesome-icons/icons/regular/house.svg?component';
    import UsersIcon from '@keyvue/one-awesome-icons/icons/regular/users.svg?component';
    import StreetViewIcon from '@keyvue/one-awesome-icons/icons/regular/street-view.svg?component';
    import ChartLineIcon from '@keyvue/one-awesome-icons/icons/regular/chart-line.svg?component';
    import MoneyBillWaveIcon from '@keyvue/one-awesome-icons/icons/regular/money-bill-wave.svg?component';
    import PiggyBankIcon from '@keyvue/one-awesome-icons/icons/regular/piggy-bank.svg?component';
    import FileInvoiceDollarIcon from '@keyvue/one-awesome-icons/icons/regular/file-invoice-dollar.svg?component';
    import PeopleGroupIcon from '@keyvue/one-awesome-icons/icons/regular/people-group.svg?component';
    import CalendarIcon from '@keyvue/one-awesome-icons/icons/regular/calendar.svg?component';
    import CommentsQuestionCheckIcon from '@keyvue/one-awesome-icons/icons/regular/comments-question-check.svg?component';
    import UserHeadsetIcon from '@keyvue/one-awesome-icons/icons/regular/user-headset.svg?component';

    import { computed, onMounted, ref, watch } from 'vue';
    import { type Assistent, type ChatConnectGoal, Interaction, InteractionId } from '@keyvue/one-api-client';
    import { type ExposedOverlay, FontSize, TextDecorator } from '@keyvue/one-ui';
    import FunctionContext from '@/ai/assistent/components/interaction/function/FunctionContext.vue';
    import SendEmailContext from '@/ai/assistent/components/interaction/function/SendEmailContext.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';

    const { client } = useClient();

    const props = defineProps<{
        assistent: Assistent,
        assistentGoals: ChatConnectGoal[],
        url: string,
        interaction: Interaction
    }>();

    const modal = ref<ExposedOverlay>();
    const localInteraction = ref<Interaction>();

    const iconForFunction = computed(() => {
        if (localInteraction.value?.functionName?.slice(0, 3) === 'cc_') {
            return CommentsQuestionCheckIcon;
        }

        switch (localInteraction.value?.functionName) {
            case 'get_datetime': return CalendarIcon;
            case 'energy_label': return BoltIcon;
            case 'streetview': return StreetViewIcon;
            case 'home_valuation': return ChartLineIcon;
            case 'maandlasten': return MoneyBillWaveIcon;
            case 'location_data': return MapIcon;
            case 'movedata': return HouseIcon;
            case 'nearest_team': return UsersIcon;
            case 'rental-movedata': return HouseIcon;
            case 'sale-movedata': return HouseIcon;
            case 'send_email': return EnvelopeIcon;
            case 'send_sms': return CommentDotsIcon;
            case 'teams': return PeopleGroupIcon;
            case 'postal_code': return MapLocationDotIcon;
            case 'woz': return FileInvoiceDollarIcon;
            case 'max_mortgage': return PiggyBankIcon;
            case 'initiate_handover': return UserHeadsetIcon;
            default: return EyeIcon;
        }
    });

    const colorForFunction = computed(() => {
        switch (localInteraction.value?.functionName) {
            default: return 'turquoise-500';
        }
    });

    const argumentsForFunction = computed(() => {
        try {
            return JSON.parse(localInteraction.value?.context?.arguments);
        } catch (e) {
            return localInteraction.value?.context?.arguments;
        }
    });

    const resultForFunction = computed(() => {
        const result = localInteraction.value?.context?.result;

        // check if result is a json string, if so, pretty print
        try {
            return JSON.stringify(JSON.parse(result), null, 2);
        } catch (e) {
            return result;
        }
    });

    const titleForFunction = computed(() => {
        const functionName = localInteraction.value?.functionName ?? localInteraction.value?.context.function ?? 'Functie';

        // eslint-disable-next-line default-case
        switch (functionName) {
            case 'energy_label': return 'Energielabel';
            case 'streetview': return 'Google streetview';
            case 'home_valuation': return 'Waardebepaling';
            case 'maandlasten': return 'Hypotheek maandlasten';
            case 'location_data': return 'Locatie gegevens';
            case 'movedata': return 'Funda check';
            case 'nearest_team': return 'Dichtstbijzijnde team';
            case 'rental-movedata': return 'Dichtstbijzijnde verhuurde woningen';
            case 'sale-movedata': return 'Dichtstbijzijnde verkochte woningen';
            case 'send_email': return 'E-mail verzonden';
            case 'send_sms': return 'SMS verzonden';
            case 'teams': return 'Gegevens vestigingen';
            case 'postal_code': return 'Postcodecheck';
            case 'woz': return 'WOZ waarde';
            case 'get_datetime': return 'Huidige datum ophalen';
            case 'max_mortgage': return 'Maximum hypotheek';
            case 'initiate_handover': return 'Handover aangevraagd';

            // ChatConnect system goals
            case 'cancel_appointment': return 'Afspraak annuleren';
            case 'schedule_appointment': return 'Afspraakverzoek';
            case 'request_more_information': return 'Meer informatie aanvragen';
        }

        let title = functionName;

        props.assistentGoals.forEach((goal) => {
            if (goal.functionName === functionName) {
                title = goal.name;
            }
        });

        return title;
    });

    const contextComponentForFunction = computed(() => {
        if (localInteraction.value?.functionName?.slice(0, 3) === 'cc_') {
            return SendEmailContext;
        }

        switch (localInteraction.value?.functionName) {
            case 'send_email': return SendEmailContext;
            default: return FunctionContext;
        }
    });

    async function showFunctionInformation() {
        await loadInteraction();
        modal.value?.openOverlay();
    }

    async function loadInteraction() {
        if (localInteraction.value?.context === undefined) {
            localInteraction.value = await client.get(Interaction, new InteractionId(props.interaction.id.value));
        }
    }

    onMounted(() => {
        localInteraction.value = props.interaction;
    });

    watch(() => props.interaction, async () => {
        await loadInteraction();
    });
</script>

<style scoped lang="scss">
    .chat-function-interaction-item {
        width: max-content;
        margin-right: 50px;
        align-self: flex-end;
        box-sizing: border-box;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: var(--theme-colors-default--turquoise-200);
        border-radius: size(xs);
        gap: size(sm);
        padding: size(sm);
        border-bottom: 1px solid var(--theme-colors-default--gray-300);
        box-shadow: 0 0 5px 0 rgba(0,0,0,0.15);

        &__title {
            font-size: font-size(sm);
        }

        &__link {
            & > button {
                display: inline-block;
                border: none;
                background: none;
                padding: 0;
                color: var(--theme-colors-default--turquoise-500);

                &:hover {
                    color: var(--button-text-hover-color);
                }
            }
        }
    }
</style>
